import { client } from "@src/apolloClient";
import { MeDocument, MeQuery } from "@src/gql/generated";

type AuthResult = {
  isAuthenticated: boolean;
  user: MeQuery["me"] | null;
};

const AUTH_ERROR_MESSAGES = ["Token expired.", "Unauthorized"];

// Shared across callers: PrivateRoute / RootRedirect (and StrictMode's double effect)
// all await the same request instead of each firing their own `Me`.
let inFlight: Promise<AuthResult> | null = null;

async function runCheckAuth(): Promise<AuthResult> {
  try {
    // Goes through Apollo so the full Me result (incl. influenceStatus) lands in the
    // cache — UserProvider then reads it cache-first without another request.
    const result = await client.query<MeQuery>({
      query: MeDocument,
      fetchPolicy: "network-only",
      errorPolicy: "all",
      // Auth failures are an expected answer here; don't let errorLink hard-redirect.
      context: { skipAuthRedirect: true },
    });

    if (result.errors?.length) {
      const hasAuthError = result.errors.some(
        (error) =>
          error.extensions?.code === "UNAUTHENTICATED" ||
          AUTH_ERROR_MESSAGES.includes(error.message),
      );
      if (hasAuthError) {
        document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      }
      return { isAuthenticated: false, user: null };
    }

    if (result.data?.me) {
      return { isAuthenticated: true, user: result.data.me };
    }
    return { isAuthenticated: false, user: null };
  } catch (error) {
    console.error("🔴 Auth check failed:", error);
    return { isAuthenticated: false, user: null };
  }
}

export function checkAuth(): Promise<AuthResult> {
  if (!inFlight) {
    inFlight = runCheckAuth().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
